import { useState } from "react";
import type { OrderListItem } from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatMxn } from "@/lib/format-mxn";

/** Matches the cancel endpoint's body cap (`orders.routes.ts`). */
const MAX_REASON_LENGTH = 200;

type Props = {
  order: OrderListItem;
  kind: "deliver" | "cancel";
  busy: boolean;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
};

/**
 * The confirmation a drag needs before it closes an order.
 *
 * Both moves are final — nothing leaves `delivered` or `cancelled` — and both
 * touch stock when inventory is tracked, so neither may happen on a slip of the
 * mouse. Moving between "Por entregar" and its sub-columns needs no dialog: it
 * changes nothing but where the card sits.
 */
export function OrderMoveDialog({
  order,
  kind,
  busy,
  onConfirm,
  onCancel,
}: Props): React.JSX.Element {
  const [reason, setReason] = useState("");
  const who = order.leadName ?? order.sessionRef;
  const total = formatMxn(order.totalCents, order.currency);

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {kind === "deliver" ? "¿Marcar como entregado?" : "¿Cancelar el pedido?"}
          </DialogTitle>
          <DialogDescription>
            {kind === "deliver"
              ? `El pedido de ${who} (${total}) queda como entregado y ya no podrá cambiar de estado.`
              : `El pedido de ${who} (${total}) queda cancelado y ya no podrá cambiar de estado.`}
          </DialogDescription>
        </DialogHeader>

        {kind === "cancel" && (
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={MAX_REASON_LENGTH}
            rows={2}
            aria-label="Razón de la cancelación"
            placeholder="Razón (opcional)"
            className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-300 focus:border-moss focus:outline-none"
          />
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-050"
          >
            Volver
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onConfirm(kind === "cancel" ? reason.trim() || undefined : undefined)}
            className={[
              "rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-40",
              kind === "deliver"
                ? "bg-lime text-moss hover:bg-lime-dk"
                : "bg-red-600 text-white hover:bg-red-700",
            ].join(" ")}
          >
            {kind === "deliver" ? "Marcar entregado" : "Cancelar pedido"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
